import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import {
  diaMes,
  diasDoPeriodo,
  gravarCelulas,
  lerCalendarioDeFerias,
  lerFerias,
  somarDias,
} from '../../escala';
import type { FeriasDaPessoaNaEscala, MudancaDeCelula } from '../../escala';

/**
 * CALENDÁRIO ANUAL DE FÉRIAS (dono 24/10/2026): "no controle precisamos ter um calendário anual pra
 * previsão de férias e lá comunicar com o quadro da loja — ele lança as férias no controle de férias
 * porque lá consegue ver todos os meses do ano, 1 ano fechado (24/10/2026 a 24/10/2027)".
 *
 * Uma linha por pessoa, uma coluna por mês; cada férias é uma barra nos dias certos. Lançar aqui
 * grava FE no QUADRO (o mesmo dado): o quadro da loja já mostra, e o saldo desconta sozinho.
 */

export const MESES = [
  'JAN',
  'FEV',
  'MAR',
  'ABR',
  'MAI',
  'JUN',
  'JUL',
  'AGO',
  'SET',
  'OUT',
  'NOV',
  'DEZ',
];
export const dataBr = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
export const umAnoDepois = (d: string) => `${String(Number(d.slice(0, 4)) + 1)}${d.slice(4)}`;
const ultimoDoMes = (ano: number, mes: number) => new Date(Date.UTC(ano, mes, 0)).getUTCDate();
export const diasEntre = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86_400_000);

/**
 * Uma cor para cada funcionário ("coloca cada cor em um funcionário", dono 24/10): o ângulo de
 * ouro espalha os tons sem repetir, e a luz alterna em 3 níveis para vizinhos não se confundirem.
 * Escuras o bastante para o nome em branco ler bem. A cor segue a pessoa (pela ordem do quadro).
 */
export function corDaPessoa(i: number): string {
  const tom = Math.round((i * 137.508) % 360);
  const luz = [34, 42, 28][i % 3] ?? 34;
  return `hsl(${String(tom)} 72% ${String(luz)}%)`;
}

export interface Mes {
  readonly ano: number;
  readonly mes: number;
  readonly de: string;
  readonly ate: string;
  readonly total: number;
}

export function mesesDoPeriodo(de: string, ate: string): Mes[] {
  const out: Mes[] = [];
  let ano = Number(de.slice(0, 4));
  let mes = Number(de.slice(5, 7));
  for (let i = 0; i < 14; i++) {
    const mm = String(mes).padStart(2, '0');
    const total = ultimoDoMes(ano, mes);
    const ini = `${String(ano)}-${mm}-01`;
    if (ini > ate) break;
    out.push({ ano, mes, de: ini, ate: `${String(ano)}-${mm}-${String(total)}`, total });
    mes += 1;
    if (mes > 12) {
      mes = 1;
      ano += 1;
    }
  }
  return out;
}

export interface Bloco {
  readonly de: string;
  readonly ate: string;
  readonly dias: number;
}

export function blocos(dias: readonly string[]): Bloco[] {
  const out: Bloco[] = [];
  for (const x of [...new Set(dias)].sort()) {
    const u = out[out.length - 1];
    if (u && diasEntre(u.ate, x) === 1) out[out.length - 1] = { ...u, ate: x, dias: u.dias + 1 };
    else out.push({ de: x, ate: x, dias: 1 });
  }
  return out;
}

export type Editando = {
  pessoa: FeriasDaPessoaNaEscala;
  bloco: Bloco | null;
  sugestao: string;
  /** Escolhido no calendário (do 1º ao último dia tocado). */
  dias?: number;
};

export function CalendarioDeFerias({ sessao, aoMudar }: { sessao: Sessao; aoMudar: () => void }) {
  const [de, setDe] = useState<string | null>(null);
  const [cal, setCal] = useState<Awaited<ReturnType<typeof lerCalendarioDeFerias>> | null>(null);
  const [pessoas, setPessoas] = useState<FeriasDaPessoaNaEscala[] | null>(null);
  const [editando, setEditando] = useState<Editando | null>(null);
  const [aviso, setAviso] = useState<{ tom: 'ok' | 'erro'; texto: string } | null>(null);

  const carregar = useCallback(() => {
    void lerCalendarioDeFerias(de ?? undefined, de ? umAnoDepois(de) : undefined).then(setCal);
    void lerFerias().then((r) => r.ok && setPessoas(r.dados.pessoas));
  }, [de]);
  useEffect(carregar, [carregar]);

  const dados = cal?.ok ? cal.dados : null;
  const meses = useMemo(() => (dados ? mesesDoPeriodo(dados.de, dados.ate) : []), [dados]);
  const porPessoa = useMemo(() => {
    const m = new Map<string, Bloco[]>();
    if (!dados) return m;
    const dias = new Map<string, string[]>();
    for (const f of dados.ferias) dias.set(f.pessoaId, [...(dias.get(f.pessoaId) ?? []), f.dia]);
    for (const [k, v] of dias) m.set(k, blocos(v));
    return m;
  }, [dados]);

  if (cal && !cal.ok) return <ErroDeArea oQue="o calendário de férias" aoTentar={carregar} />;
  if (!dados || !pessoas) return <div className="qh-carregando">Montando o ano…</div>;

  const andar = (meses: number) => {
    const base = new Date(`${dados.de}T12:00:00Z`);
    base.setUTCMonth(base.getUTCMonth() + meses);
    setDe(base.toISOString().slice(0, 10));
  };

  const lancados = dados.ferias.length;
  const fora = (d: string) => d < dados.de || d > dados.ate;

  return (
    <div className="qh-corpo">
      <div className="qh-barra">
        <div className="qh-periodo">
          <button
            type="button"
            className="qh-btn-icone"
            onClick={() => andar(-1)}
            aria-label="Um mês para trás"
          >
            <Icone nome="arrow-l" tamanho={16} />
          </button>
          <span className="qh-periodo-txt">
            {dataBr(dados.de)} a {dataBr(dados.ate)}
          </span>
          <button
            type="button"
            className="qh-btn-icone"
            onClick={() => andar(1)}
            aria-label="Um mês para frente"
          >
            <Icone nome="arrow" tamanho={16} />
          </button>
          {de && (
            <button
              type="button"
              className="qh-btn qh-btn-sec qh-btn-curto"
              onClick={() => setDe(null)}
            >
              Hoje
            </button>
          )}
        </div>
        <span className="qh-mudo">
          {lancados} dias de férias no período · toque no mês da pessoa para lançar
        </span>
      </div>
      {aviso && (
        <div
          className={`qh-aviso qh-aviso-${aviso.tom}`}
          role={aviso.tom === 'erro' ? 'alert' : 'status'}
        >
          {aviso.texto}
          <button type="button" onClick={() => setAviso(null)} aria-label="Fechar aviso">
            <Icone nome="x" tamanho={14} />
          </button>
        </div>
      )}
      <div className="qh-rolagem">
        <table className="qh-cal">
          <thead>
            <tr>
              <th className="qh-cal-nome">Pessoa</th>
              <th className="qh-cal-saldo">Saldo</th>
              {meses.map((m) => (
                <th key={m.de} className={m.mes === 1 ? 'qh-cal-ano' : undefined}>
                  {MESES[m.mes - 1]}
                  <small>/{String(m.ano).slice(2)}</small>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pessoas.map((p, i) => {
              const bl = porPessoa.get(p.pessoaId) ?? [];
              // a mesma cor do calendário do ano (a ordem das pessoas é a mesma)
              const corP = corDaPessoa(i);
              const v = p.ferias?.proximoVencimento;
              const urgente = v && diasEntre(dados.hoje, v.limite) <= 60;
              return (
                <tr key={p.pessoaId}>
                  <th className="qh-cal-nome" scope="row">
                    <span className="qh-cor qh-cor-nome" style={{ background: corP }} />
                    {p.nome}
                  </th>
                  <td className={`qh-cal-saldo${urgente ? ' qh-cal-urgente' : ''}`}>
                    {p.ferias ? (
                      <>
                        <strong>{p.ferias.saldo}</strong>
                        {v && <small>até {diaMes(v.limite)}</small>}
                      </>
                    ) : (
                      <small title="Sem data de admissão">—</small>
                    )}
                  </td>
                  {meses.map((m) => {
                    const doMes = bl.filter((b) => b.ate >= m.de && b.de <= m.ate);
                    const hojeAqui = dados.hoje >= m.de && dados.hoje <= m.ate;
                    return (
                      <td
                        key={m.de}
                        className={`qh-cal-mes${m.mes === 1 ? ' qh-cal-ano' : ''}`}
                        onClick={() =>
                          setEditando({
                            pessoa: p,
                            bloco: null,
                            sugestao: m.de < dados.hoje ? dados.hoje : m.de,
                          })
                        }
                        title={`${p.nome} · ${MESES[m.mes - 1] ?? ''}/${String(m.ano)} — toque para lançar férias`}
                      >
                        {hojeAqui && (
                          <span
                            className="qh-cal-hoje"
                            style={{
                              left: `${String(((Number(dados.hoje.slice(8)) - 0.5) / m.total) * 100)}%`,
                            }}
                          />
                        )}
                        {doMes.map((b) => {
                          const ini = b.de < m.de ? 1 : Number(b.de.slice(8));
                          const fim = b.ate > m.ate ? m.total : Number(b.ate.slice(8));
                          return (
                            <button
                              key={b.de}
                              type="button"
                              className={`qh-cal-barra${b.de < dados.hoje ? ' qh-cal-passada' : ''}`}
                              style={{
                                background: corP,
                                left: `${String(((ini - 1) / m.total) * 100)}%`,
                                width: `${String(((fim - ini + 1) / m.total) * 100)}%`,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditando({ pessoa: p, bloco: b, sugestao: b.de });
                              }}
                              title={`${p.nome}: férias ${diaMes(b.de)} a ${diaMes(b.ate)} (${String(b.dias)} dias)`}
                            >
                              {ini === Number(b.de.slice(8)) && !fora(b.de) && fim - ini >= 4
                                ? String(b.dias)
                                : ''}
                            </button>
                          );
                        })}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="qh-mudo">
        As barras são as férias marcadas no quadro (FE). Lançar aqui grava no quadro da loja na
        hora. O número dentro da barra é a quantidade de dias.
      </p>
      {editando && (
        <LancarFerias
          e={editando}
          outras={dados.outras.filter((o) => o.pessoaId === editando.pessoa.pessoaId)}
          aoFechar={() => setEditando(null)}
          aoGravar={async (mudancas, texto) => {
            const r = await gravarCelulas(sessao, mudancas);
            setEditando(null);
            if (r.tipo === 'confirmada') {
              setAviso({ tom: 'ok', texto });
              carregar();
              aoMudar();
            } else
              setAviso({
                tom: 'erro',
                texto: r.tipo === 'recusada' ? r.mensagem : 'A internet caiu no meio — confira.',
              });
          }}
        />
      )}
    </div>
  );
}

export function LancarFerias({
  e,
  outras,
  aoFechar,
  aoGravar,
}: {
  e: Editando;
  outras: readonly { dia: string; turno: string }[];
  aoFechar: () => void;
  aoGravar: (m: MudancaDeCelula[], texto: string) => Promise<void>;
}) {
  const [de, setDe] = useState(e.bloco?.de ?? e.sugestao);
  const [dias, setDias] = useState(e.bloco?.dias ?? e.dias ?? 10);
  const [salvando, setSalvando] = useState(false);
  const ate = somarDias(de, Math.max(1, dias) - 1);
  const saldo = e.pessoa.ferias?.saldo ?? null;
  // o saldo já conta o bloco que está sendo mudado
  // o saldo de hoje já desconta o bloco que está sendo mudado: devolve antes de comparar
  const saldoAntes = saldo === null ? null : saldo + (e.bloco?.dias ?? 0);
  const saldoDepois = saldoAntes === null ? null : saldoAntes - dias;
  const conflitos = outras.filter((o) => o.dia >= de && o.dia <= ate);

  const salvar = () => {
    setSalvando(true);
    const novos = new Set(diasDoPeriodo(de, ate));
    const m: MudancaDeCelula[] = [];
    // os dias que saem do bloco antigo ficam vazios (a folga fixa pode preencher depois)
    if (e.bloco)
      for (const d of diasDoPeriodo(e.bloco.de, e.bloco.ate))
        if (!novos.has(d)) m.push({ pessoaId: e.pessoa.pessoaId, dia: d, turno: null });
    for (const d of novos) m.push({ pessoaId: e.pessoa.pessoaId, dia: d, turno: 'FE' });
    void aoGravar(
      m,
      `${e.pessoa.nome}: férias de ${diaMes(de)} a ${diaMes(ate)} (${String(dias)} dias) — já está no quadro.`,
    );
  };
  const desfazer = () => {
    if (!e.bloco) return;
    if (
      !window.confirm(
        `Desfazer as férias de ${e.pessoa.nome} (${diaMes(e.bloco.de)} a ${diaMes(e.bloco.ate)})?`,
      )
    )
      return;
    setSalvando(true);
    void aoGravar(
      diasDoPeriodo(e.bloco.de, e.bloco.ate).map((d) => ({
        pessoaId: e.pessoa.pessoaId,
        dia: d,
        turno: null,
      })),
      `${e.pessoa.nome}: férias desfeitas — os dias voltaram a ficar vazios no quadro.`,
    );
  };

  return (
    <div className="qh-modal-fundo" onClick={aoFechar} role="presentation">
      <div
        className="qh-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Férias de ${e.pessoa.nome}`}
        onClick={(ev) => ev.stopPropagation()}
      >
        <header>
          <div>
            <h2>
              {e.bloco ? 'Mudar férias' : 'Lançar férias'} · {e.pessoa.nome}
            </h2>
            {saldo === null && (
              <p>
                Sem data de admissão — cadastre em “Saldos e dias tirados” para conferir o saldo.
              </p>
            )}
          </div>
          <button type="button" className="qh-btn-icone" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={16} />
          </button>
        </header>
        {saldoAntes !== null && saldoDepois !== null && (
          <>
            {/* "na hora de pôr as férias, aparecer quantas férias restam dele" (dono 24/10) */}
            <div className="qh-saldo-grande">
              <div>
                <span>Disponível agora</span>
                <strong>{saldoAntes}</strong>
                <small>dias</small>
              </div>
              <div>
                <span>Este lançamento</span>
                <strong>−{dias}</strong>
                <small>dias</small>
              </div>
              <div className={saldoDepois < 0 ? 'qh-saldo-ruim' : 'qh-saldo-bom'}>
                <span>Restam depois</span>
                <strong>{saldoDepois}</strong>
                <small>dias</small>
              </div>
            </div>
            <ul className="qh-de-onde">
              {(e.pessoa.ferias?.periodos ?? [])
                .filter((p) => p.situacao !== 'EM_AQUISICAO' && p.saldo > 0)
                .map((p) => (
                  <li
                    key={p.inicio}
                    className={
                      p.situacao === 'VENCIDO' || p.situacao === 'VENCENDO'
                        ? 'qh-de-onde-alerta'
                        : undefined
                    }
                  >
                    Período {p.inicio.slice(0, 4)}/{p.fim.slice(2, 4)}:{' '}
                    <strong>{p.saldo} dias</strong> · tirar até {dataBr(p.limite)}
                    {p.situacao === 'VENCIDO' && ' (vencido)'}
                  </li>
                ))}
              {(e.pessoa.ferias?.periodos ?? [])
                .filter((p) => p.situacao === 'EM_AQUISICAO')
                .map((p) => (
                  <li key={p.inicio} className="qh-de-onde-futuro">
                    Ganha mais {p.direito - p.usados > 0 ? p.direito - p.usados : 0} dias em{' '}
                    {dataBr(somarDias(p.fim, 1))}
                  </li>
                ))}
            </ul>
          </>
        )}
        <div className="qh-linha-campos">
          <label className="qh-campo">
            Começa em
            <input
              type="date"
              value={de}
              onChange={(ev) => ev.target.value && setDe(ev.target.value)}
            />
          </label>
          <label className="qh-campo">
            Quantos dias
            <input
              type="number"
              min={1}
              max={60}
              value={dias}
              onChange={(ev) => setDias(Math.min(60, Math.max(1, Number(ev.target.value) || 1)))}
            />
          </label>
        </div>
        <div className="qh-atalhos">
          {[3, 5, 10, 15, 20, 30].map((n) => (
            <button
              key={n}
              type="button"
              className={`qh-btn qh-btn-curto ${dias === n ? 'qh-btn-pri' : 'qh-btn-sec'}`}
              onClick={() => setDias(n)}
            >
              {n} dias
            </button>
          ))}
        </div>
        <p className="qh-previa">
          <strong>
            {diaMes(de)} a {diaMes(ate)}
          </strong>{' '}
          · {dias} dias · volta ao trabalho em {dataBr(somarDias(ate, 1))}
        </p>
        {saldoDepois !== null && saldoDepois < 0 && (
          <p className="qh-aviso qh-aviso-erro">
            Passa do saldo em {-saldoDepois} dias (férias adiantadas).
          </p>
        )}
        {conflitos.length > 0 && (
          <p className="qh-aviso qh-aviso-erro">
            Vai passar por cima de{' '}
            {conflitos.map((c) => `${c.turno} em ${diaMes(c.dia)}`).join(', ')}.
          </p>
        )}
        <footer>
          {e.bloco && (
            <button
              type="button"
              className="qh-btn qh-btn-perigo"
              onClick={desfazer}
              disabled={salvando}
            >
              Desfazer férias
            </button>
          )}
          <button type="button" className="qh-btn qh-btn-sec" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="qh-btn qh-btn-pri" onClick={salvar} disabled={salvando}>
            {e.bloco ? 'Salvar' : 'Lançar no quadro'}
          </button>
        </footer>
      </div>
    </div>
  );
}
